import { useInkind } from '@gik/api/inkinds/inkind';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { Button, IconSetType, SvgIcon } from '@gik/ui';
import React from 'react';
import { useClickAway, usePrevious } from 'react-use';
import { useTranslation } from 'react-i18next';
import { colorNameTranslationPrefix, translationKeys } from '@gik/inkind-page/i18n/en';
import { removeInkindCoverPhoto } from '@gik/api/inkinds/inkindCoverPhoto';
import { patchInkind } from '@gik/api/inkinds/inkinds';
import sleep from '@gik/core/utils/sleep';
import { optimisticUpdate } from '@gik/core/api';

interface IInkindCoverToolbarProps {
  inkindRouteId: string;

  onClickAddCoverPhoto(): void;

  onCoverColourChange(): void;
}

interface IInkindCoverToolbarButtonProps {
  onClick: () => void;
  icon?: IconSetType;
  className?: string;
  tooltip?: string;
  isClosingToolbar?: boolean;
}

function InkindCoverToolbarButton({
  onClick,
  icon,
  className,
  tooltip,
  isClosingToolbar,
}: IInkindCoverToolbarButtonProps) {
  const bem = useBemCN('inkind-cover-toolbar-button');
  return (
    <div {...bem(undefined, [{ closing: isClosingToolbar }])}>
      <Button
        type="button"
        variant="secondary"
        noFocusStyling
        tooltip={tooltip}
        circle
        {...bem('button', undefined, className)}
        onClick={onClick}
      >
        {icon && <SvgIcon {...icon} />}
      </Button>
    </div>
  );
}

export default function InkindCoverToolbar({
  inkindRouteId,
  onClickAddCoverPhoto,
  onCoverColourChange,
}: IInkindCoverToolbarProps) {
  const { data: inkindPage, mutate: mutateInkindPage } = useInkind(inkindRouteId);
  const [isToolbarOpen, setToolbarOpen] = React.useState(false);
  const prevIsToolbarOpen = usePrevious(isToolbarOpen);
  const [isClosingToolbar, setIsClosingToolbar] = React.useState(false);
  const { t } = useTranslation();

  React.useEffect(() => {
    if (prevIsToolbarOpen && !isToolbarOpen) {
      (async () => {
        setIsClosingToolbar(true);
        await sleep(1000);
        setIsClosingToolbar(false);
      })();
    }
  }, [prevIsToolbarOpen, isToolbarOpen, setIsClosingToolbar]);

  const toolbarRef = React.useRef(null);
  useClickAway(toolbarRef, () => {
    setToolbarOpen(false);
  });
  const bem = useBemCN('inkind-cover-toolbar');
  const hasPhoto = !!inkindPage?.cover_src;

  function removeCoverPhoto() {
    optimisticUpdate(
      {
        ...inkindPage,
        cover_src: null,
        cover_src_retina: null,
      },
      mutateInkindPage,
      () => removeInkindCoverPhoto(inkindRouteId)
    );

    setToolbarOpen(false);
  }

  async function selectGradient(coverColourIndex: number) {
    optimisticUpdate(
      {
        ...inkindPage,
        coverColourIndex,
      },
      mutateInkindPage,
      () => patchInkind(inkindRouteId, { coverColourIndex })
    );
    setToolbarOpen(false);
    onCoverColourChange();
  }

  return (
    <div ref={toolbarRef} {...bem()}>
      <InkindCoverToolbarButton
        onClick={() => setToolbarOpen(!isToolbarOpen)}
        icon={{
          set: 'gik-icons',
          name: 'CoverSettings',
        }}
        tooltip={t(translationKeys.PageHeaderChangeBackground)}
      />
      {(isToolbarOpen || isClosingToolbar || prevIsToolbarOpen) && (
        <>
          <InkindCoverToolbarButton
            isClosingToolbar={isClosingToolbar}
            onClick={hasPhoto ? removeCoverPhoto : onClickAddCoverPhoto}
            icon={{
              set: 'hero-icons',
              name: hasPhoto ? 'X' : 'Camera',
            }}
            tooltip={t(hasPhoto ? translationKeys.PageHeaderRemovePhoto : translationKeys.PageHeaderChoosePhoto)}
          />
          {[0, 1, 2, 3, 4, 5].map(gradientIndex => (
            <InkindCoverToolbarButton
              isClosingToolbar={isClosingToolbar}
              key={gradientIndex}
              onClick={() => selectGradient(gradientIndex)}
              className={`gradient${gradientIndex}`}
              tooltip={t(translationKeys[colorNameTranslationPrefix + gradientIndex])}
            />
          ))}
        </>
      )}
    </div>
  );
}
